import { isArticleCtaUtm } from '@/lib/attribution';

// Google Analytics 4 (GA4).
//
// gtag.js is loaded statically in index.html <head> (so it's detectable by
// Google's installation checker and fires immediately). These helpers report
// SPA page_views on route changes and custom conversion events. They are safe
// no-ops if gtag isn't present yet.

// Query keys that identify a person or grant access. The journey resumes from
// ?candidateId=<uuid>&access=<token>, and GA4 was storing both under page_path
// and page_location — a candidate id that, with the table's public read
// policy, is a key to the whole record. Strip them; keep utm_* and the rest.
const SENSITIVE_PARAMS = /^(candidateId|access|token|code|email|phone)$/i;

// Our own article CTAs carry utm_source=blog&utm_medium=article (growth step 3)
// so the LEAD row can say which article converted. GA4 must not see them: a
// campaign on page_location re-credits the visit to "blog / article", and the
// Google search that actually brought the visitor would lose the conversion.
// The article still reaches GA4, as generate_lead's article_slug parameter.
export const sanitizeAnalyticsPath = (pathname, search = '') => {
  const kept = new URLSearchParams();
  const params = new URLSearchParams(search || '');
  const internalCta = isArticleCtaUtm(params);
  params.forEach((v, k) => {
    if (SENSITIVE_PARAMS.test(k)) return;
    if (internalCta && /^utm_/i.test(k)) return;
    kept.append(k, v);
  });
  const q = kept.toString();
  return `${pathname || '/'}${q ? `?${q}` : ''}`;
};

export const trackPageview = (path) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  // page_location is rebuilt from the sanitised path, never window.location.href.
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
};

export const trackEvent = (name, params = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', name, params);
};

// ─── Conversion events, fired at most once per candidate ────────────────────
//
// The funnel's six conversion events are reached by URLs the candidate can
// reload or navigate back to. The Didit return is the clearest case: the
// journey resumes from ?candidateId=…&resume=didit, and the effect that fires
// identity_verified is guarded only by a useRef — which resets on remount, so
// a reload of that URL counts the same verification again.
//
// A double-counted conversion is worse than a missing one: it inflates exactly
// the number ad spend is optimised against. These helpers make each event
// idempotent per candidate, so a stage counts once however often its page is
// re-entered.
//
// localStorage is per-browser, so a candidate who switches device is counted
// twice — that is the same limitation GA4's own client id has, and far smaller
// than the reload case. Every access is wrapped: private mode and blocked
// site-data both throw, and analytics must never break the funnel.

const seenKey = (name, id) => `pl_ev:${name}:${id || 'anon'}`;

const alreadySent = (name, id) => {
  try {
    return window.localStorage.getItem(seenKey(name, id)) !== null;
  } catch {
    return false; // can't remember → fall back to firing, never to blocking
  }
};

const markSent = (name, id) => {
  try {
    window.localStorage.setItem(seenKey(name, id), String(Date.now()));
  } catch {
    /* storage unavailable — the event still fired, it just isn't deduped */
  }
};

/**
 * Fire a conversion event at most once per candidate.
 * @param {string} name   GA4 event name
 * @param {string} id     candidate id — the dedupe scope
 * @param {object} params event parameters
 * @returns {boolean}     whether it was sent this time
 */
export const trackConversionOnce = (name, id, params = {}) => {
  if (typeof window === 'undefined') return false;
  if (alreadySent(name, id)) return false;
  markSent(name, id);
  trackEvent(name, params);
  return true;
};
